import { useMemo, useState } from "react";
import { FiPlus, FiUsers } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { Pagination } from "newui/components/Pagination";
import { Help } from "newui/components/ui/Help";
import {
  TOUR_USERS,
  buildUserListSteps,
  isTourDummyUser,
} from "guides/features/Auth";
import { useHasRbacV2 } from "hooks/featureFlags";
import { useUserAssignments } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { AssignWizard } from "newui/features/Settings/components/RolesPermissions/AssignWizard/AssignWizard";
import { AssignRoleDrawer } from "newui/features/Settings/components/RolesPermissions/AssignRoleDrawer";
import { UserAccessMatrixModal } from "newui/features/Settings/components/RolesPermissions/UserAccessMatrixModal";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import type { UserListItem, UserListFilters } from "../../types/userInvite";
import { UserListTable } from "./UserListTable/UserListTable";
import { UserListFiltersComponent } from "./UserListTable/UserListFilters";
import { UserDrawer } from "./UserDrawer";
import { UserStatusConfirmModal } from "./UserStatusConfirmModal";
import { useUsersQuery } from "./hooks/useUsersQuery";
import { useDeactivateUserMutation } from "./hooks/useDeactivateUserMutation";

export const UserListPage = () => {
  const [filters, setFilters] = useState<UserListFilters>({
    page: 1,
    status: ["active", "invited"],
  });
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<UserListItem | null>(null);
  // When the guided tour is active we inject demo rows (see TOUR_USERS) so
  // the steps have stable targets for resend / deactivate / reactivate.
  const [isTourActive, setIsTourActive] = useState(false);

  // PRO-413: RBAC v2 is ADDITIVE. When on, we layer role management (role pills,
  // per-user "Manage roles" drawer, bulk "Assign template" wizard) onto this page
  // without removing any of the existing user-management functionality.
  const hasRbacV2 = useHasRbacV2();
  const [isAssignWizardOpen, setIsAssignWizardOpen] = useState(false);
  const [manageRolesTarget, setManageRolesTarget] = useState<UserAssignment | null>(null);
  // PRO-413 #UM1: the user whose read-only effective-access matrix is open.
  const [accessMatrixTarget, setAccessMatrixTarget] = useState<UserListItem | null>(null);
  // Users pre-selected when the wizard opens. Empty = the bulk "Allocate roles"
  // entry point (pick any users); seeded with one id when jumping from a single
  // user's "Manage roles" drawer to scope their role to projects/RFx events.
  const [wizardInitialUserIds, setWizardInitialUserIds] = useState<number[]>([]);

  const { data, isLoading } = useUsersQuery(filters);
  const deactivate = useDeactivateUserMutation();

  // RBAC assignment records, keyed by user id, so each legacy row can render its
  // roles. Ignored entirely when the flag is off.
  const { users: assignmentUsers } = useUserAssignments({ enabled: hasRbacV2 });
  const assignmentsByUserId = useMemo(() => {
    const map = new Map<number, UserAssignment>();
    if (hasRbacV2) {
      assignmentUsers.forEach((assignment) => map.set(assignment.id, assignment));
    }
    return map;
  }, [hasRbacV2, assignmentUsers]);

  // Re-derive the manage-roles target from the LIVE assignments map so the drawer's
  // "Current roles" reflects server state after an assign/remove mutation invalidates
  // the query — `manageRolesTarget` itself is only the snapshot captured at open time.
  const manageRolesUser = useMemo(() => {
    if (manageRolesTarget === null) {
      return null;
    }
    return assignmentsByUserId.get(manageRolesTarget.id) ?? manageRolesTarget;
  }, [manageRolesTarget, assignmentsByUserId]);

  const helpSteps = useMemo(
    () =>
      buildUserListSteps({
        openInviteDrawer: () => setIsInviteDrawerOpen(true),
        closeInviteDrawer: () => setIsInviteDrawerOpen(false),
      }),
    [],
  );

  const realUsers = data?.data ?? [];
  const users = isTourActive ? [...TOUR_USERS, ...realUsers] : realUsers;

  const handleConfirmDeactivate = () => {
    if (!deactivateTarget) {
      return;
    }
    // Dummy tour rows must never hit the API.
    if (isTourDummyUser(deactivateTarget)) {
      setDeactivateTarget(null);
      return;
    }
    deactivate.mutate(deactivateTarget.id, {
      onSettled: () => setDeactivateTarget(null),
    });
  };

  const handleEdit = (user: UserListItem) => {
    // Dummy tour rows don't have a backing record — don't open the edit drawer.
    if (isTourDummyUser(user)) {
      return;
    }
    setEditTarget(user);
  };

  const handleDeactivate = (user: UserListItem) => {
    if (isTourDummyUser(user)) {
      return;
    }
    setDeactivateTarget(user);
  };

  const openBulkAssign = () => {
    setWizardInitialUserIds([]);
    setIsAssignWizardOpen(true);
  };

  const closeAssignWizard = () => {
    setIsAssignWizardOpen(false);
    setWizardInitialUserIds([]);
  };

  // Jump from a single user's "Manage roles" drawer straight into the wizard with
  // that user pre-selected, so their role can be scoped to projects/RFx events.
  const handleScopeToProjects = (assignment: UserAssignment) => {
    setManageRolesTarget(null);
    setWizardInitialUserIds([assignment.id]);
    setIsAssignWizardOpen(true);
  };

  const handleManageRoles = (user: UserListItem) => {
    if (isTourDummyUser(user)) {
      return;
    }
    // Prefer the server-authoritative assignment record; fall back to a minimal
    // shell so a not-yet-assigned user can still be given their first role.
    const existing = assignmentsByUserId.get(user.id);
    setManageRolesTarget(
      existing ?? {
        id: user.id,
        name: `${user.first_name} ${user.last_name}`.trim(),
        email: user.email,
        migration_status: "pending",
        assignments: [],
        flags: { template_creation: false },
      },
    );
  };

  const handleViewAccess = (user: UserListItem) => {
    if (isTourDummyUser(user)) {
      return;
    }
    setAccessMatrixTarget(user);
  };

  return (
    <div className="rounded-lg bg-white shadow-sm border border-gray-200">
      <div className="flex items-center justify-between gap-4 px-4 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div>
            <Help
              parent={<h2 className="text-lg font-semibold text-gray-900">User Management</h2>}
              guideSteps={helpSteps}
              label="Show me how to invite and manage users"
              data-testid="user-list-help"
              onBeforeShowMe={() => {
                setIsTourActive(true);
              }}
              onAfterShowMe={() => {
                setIsTourActive(false);
                setIsInviteDrawerOpen(false);
              }}
            />
            <p className="text-sm text-gray-500">Manage your company users and their access.</p>
          </div>
        </div>
        <ReadOnlyGuard>
          <div className="flex items-center gap-2">
            {hasRbacV2 && (
              <Button
                btntype="plain-border"
                onClick={openBulkAssign}
                className="flex items-center gap-2"
                data-testid="open-assign-wizard"
              >
                <FiUsers className="h-4 w-4" />
                Allocate Roles
              </Button>
            )}
            <Button
              onClick={() => setIsInviteDrawerOpen(true)}
              className="flex items-center gap-2"
              data-testid="invite-user-button"
            >
              <FiPlus className="h-4 w-4" />
              Invite User
            </Button>
          </div>
        </ReadOnlyGuard>
      </div>

      <UserListFiltersComponent filters={filters} onChange={(f) => setFilters({ ...f, page: 1 })} />

      <div data-testid="user-list-table">
        <UserListTable
          users={users}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          rbacEnabled={hasRbacV2}
          assignmentsByUserId={assignmentsByUserId}
          onManageRoles={handleManageRoles}
          onViewAccess={handleViewAccess}
        />
      </div>

      <Pagination
        currentPage={filters.page ?? 1}
        rowsPerPage={data?.meta?.per_page ?? 25}
        totalItems={data?.meta?.total ?? 0}
        onPageChange={(newPage) => setFilters((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={() => undefined}
        disabled={isLoading}
      />

      {/* Unified drawer: invite (ghost two-step) and edit share one component.
          onManageRoles/onViewAccess intentionally do NOT close this drawer —
          the role/scope drawer stacks on top and returns the admin to their
          in-progress form (invite draft or edit) with state intact. */}
      <UserDrawer
        mode="invite"
        isOpen={isInviteDrawerOpen}
        onClose={() => setIsInviteDrawerOpen(false)}
        rbacEnabled={hasRbacV2}
        onManageRoles={handleManageRoles}
        onViewAccess={handleViewAccess}
      />

      <UserDrawer
        mode="edit"
        user={editTarget}
        isOpen={editTarget !== null}
        onClose={() => setEditTarget(null)}
        rbacEnabled={hasRbacV2}
        onManageRoles={handleManageRoles}
        onViewAccess={handleViewAccess}
      />

      {hasRbacV2 && (
        <>
          <AssignWizard
            isOpen={isAssignWizardOpen}
            onClose={closeAssignWizard}
            users={assignmentUsers}
            initialUserIds={wizardInitialUserIds}
          />
          <AssignRoleDrawer
            isOpen={manageRolesUser !== null}
            onClose={() => setManageRolesTarget(null)}
            user={manageRolesUser}
            onScopeToProjects={
              manageRolesUser
                ? () => handleScopeToProjects(manageRolesUser)
                : undefined
            }
          />
          <UserAccessMatrixModal
            isOpen={accessMatrixTarget !== null}
            userId={accessMatrixTarget?.id}
            userName={
              accessMatrixTarget
                ? `${accessMatrixTarget.first_name} ${accessMatrixTarget.last_name}`.trim()
                : undefined
            }
            onClose={() => setAccessMatrixTarget(null)}
          />
        </>
      )}

      {deactivateTarget && (
        <UserStatusConfirmModal
          isOpen
          action="deactivate"
          firstName={deactivateTarget.first_name}
          isLoading={deactivate.isPending}
          onConfirm={handleConfirmDeactivate}
          onClose={() => setDeactivateTarget(null)}
        />
      )}
    </div>
  );
};
